import type { PaginationMetadata, PaginationQuery } from './common'

export const TRANSFORMATION_TYPES = ['repack', 'production'] as const
export type TransformationType = (typeof TRANSFORMATION_TYPES)[number]

export const TRANSFORMATION_STATUSES = ['draft', 'completed', 'cancelled'] as const
export type TransformationStatus = (typeof TRANSFORMATION_STATUSES)[number]

export const TRANSFORMATION_STATUS_LABELS: Record<
  TransformationStatus,
  { label: string; variant: 'default' | 'outline' | 'secondary' | 'destructive' }
> = {
  draft: { label: 'Draft', variant: 'outline' },
  completed: { label: 'Selesai', variant: 'default' },
  cancelled: { label: 'Dibatalkan', variant: 'destructive' },
}

export const TRANSFORMATION_DIRECTIONS = ['input', 'output'] as const
export type TransformationDirection = (typeof TRANSFORMATION_DIRECTIONS)[number]

export interface StockTransformationLine {
  id: string
  transformationId: string
  direction: TransformationDirection
  itemType: 'raw_material' | 'product'
  itemId: string
  itemName: string
  itemSku?: string | null
  uomId: string
  uomName: string
  qty: number
  /** Repack output only: packages × pack size; qty below this is shrinkage. */
  expectedQty?: number | null
  unitCost: number
  /** Production input only: the output line (menu) this ingredient went into. */
  outputLineId?: string | null
  note?: string | null
  createdAt: string
}

export interface StockTransformation {
  id: string
  workspaceId: string
  code: string
  date: string
  type: TransformationType
  status: TransformationStatus
  note?: string | null
  cancelReason?: string | null
  totalInputQty: number
  totalOutputQty: number
  shrinkageQty: number
  totalInputCost: number
  totalOutputCost: number
  completedAt?: string | null
  createdBy?: string | null
  createdByName?: string | null
  lines: StockTransformationLine[]
  createdAt: string
  updatedAt: string
}

export interface CreateRepackReq {
  date?: string
  /** A purchased (barang beli) or direct-use item, taken from the warehouse (on_hand). */
  sourceRawMaterialId: string
  /** Packages opened (purchased) or pieces moved to the bar (direct), in the source's unit. */
  inputQty: number
  /** What actually came out, in the output's unit; defaults to the full expected yield. */
  outputQty?: number | null
  note?: string | null
}

export interface CancelTransformationReq {
  cancelReason: string
}

export interface ProductionPrefillItem {
  rawMaterialId: string
  rawMaterialName: string
  rawMaterialSku?: string | null
  uomId: string
  uomName: string
  recipeQty: number
  requiredQty: number
  unitCost: number
  subtotalCost: number
  availableStock: number
  isShortage: boolean
}

export interface ProductionPrefillRes {
  productId: string
  productName: string
  productSku?: string | null
  recipeId: string
  recipeVersion: number
  yieldQty: number
  yieldUomId?: string | null
  yieldUomName?: string | null
  targetYieldQty: number
  items: ProductionPrefillItem[]
  estimatedTotalCost: number
  estimatedUnitCost: number
}

export interface CreateProductionInputLineReq {
  rawMaterialId: string
  qty: number
  uomId?: string
  note?: string | null
}

/** One menu in a production batch, with the ingredients it is made from. */
export interface CreateProductionOutputReq {
  productId: string
  targetYieldQty: number
  inputs: CreateProductionInputLineReq[]
}

export interface CreateProductionReq {
  date?: string
  outputs: CreateProductionOutputReq[]
  note?: string | null
}

export interface ProductionActualOutput {
  /** The batch's output line. */
  lineId: string
  qty: number
}

export interface CompleteProductionReq {
  /** What actually came out per menu; a menu left out counts as its target. */
  actualOutputs?: ProductionActualOutput[]
  note?: string | null
}

export interface GetTransformationListReq extends PaginationQuery {
  type?: TransformationType
  status?: TransformationStatus
  search?: string
  startDate?: string
  endDate?: string
}

export interface TransformationListRes {
  items: StockTransformation[]
  pagination: PaginationMetadata
}

export interface RepackShrinkageItem {
  rawMaterialId: string
  rawMaterialName: string
  rawMaterialSku?: string | null
  uomId: string
  uomName: string
  uomSymbol?: string | null
  expectedQty: number
  actualQty: number
  shrinkageQty: number
  shrinkagePercentage: number
  shrinkageValue: number
  documentCount: number
}

export interface RepackShrinkageReportRes {
  items: RepackShrinkageItem[]
  totalShrinkageValue: number
}

export interface GetRepackShrinkageReq {
  startDate?: string
  endDate?: string
  rawMaterialId?: string
  workspaceId?: string
}


